import type { FC, SyntheticEvent } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

export type PaginationProps = {
	/** Array of pages to display */
	pages: number[];
	/** Index of the currently selected page (0-based) */
	selectedIndex?: number;
	/** Aria label for the pagination navigation */
	label?: string;
	/** Aria label for individual page buttons */
	pageLabel?: string;
	/** Aria label for the previous button */
	previousLabel?: string;
	/** Aria label for the next button */
	nextLabel?: string;
	/** Callback when page changes */
	onChange?: (event: SyntheticEvent, page: number) => void;
};

// WAC Foundations "Page-level Pagination" spec. Two sizes, no prop: Large from the
// `sm` breakpoint up (20px numbers, 24px between arrows and numbers, 32px around),
// Regular below it (16px numbers, 16px between arrows and numbers, 4px sides).
// Below `xs` the gallery mock also drops every page except the first, last and
// current one, and tightens the gap to 8px, so the control fits a 375px viewport.
const FONT_WEIGHT_SEMIBOLD = 600;
const ELLIPSIS_OPACITY = 0.6;

const baseStyles = css({
	display: 'inline-flex',
	padding: token('space.400'), // 32px padding on all sides
	alignItems: 'center',
	justifyContent: 'center',
	fontFamily: wacToken('font.charlie.text'),
	background: wacToken('color.neutral.light0'),
	boxSizing: 'border-box',
	[media.below.sm]: {
		paddingInlineStart: token('space.050'), // 4px
		paddingInlineEnd: token('space.050'),
	},
});

const innerContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.300'), // 24px between arrows and page numbers
	boxSizing: 'border-box',
	[media.below.sm]: {
		gap: token('space.200'), // 16px
	},
});

const pagesContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'), // 16px between number cells
	[media.below.xs]: {
		gap: token('space.100'), // 8px
	},
});

const numberTextStyles = css({
	fontFamily: 'inherit',
	fontSize: '20px',
	lineHeight: '20px',
	fontWeight: FONT_WEIGHT_SEMIBOLD,
	[media.below.sm]: {
		fontSize: '16px',
	},
});

const pageButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	border: 'none',
	background: 'transparent',
	cursor: 'pointer',
	borderRadius: '50%',
	width: token('space.400'), // 32px
	height: token('space.400'), // 32px
	padding: '0',
	color: wacToken('color.neutral.light800'),
	textDecoration: 'none',

	// Hover effects only for non-selected pages
	"&:hover:not([aria-current='page'])": {
		color: wacToken('color.primary.blue'),
	},

	'&:focus-visible': {
		outline: `${token('space.025')} solid ${wacToken('color.primary.blue')}`, // 2px
		outlineOffset: token('space.025'), // 2px
	},

	"&[aria-current='page']": {
		backgroundColor: wacToken('color.primary.black'),
		color: wacToken('color.neutral.light0'),
	},
});

const mobileHiddenStyles = css({
	[media.below.xs]: {
		display: 'none',
	},
});

const navigatorStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	border: 'none',
	background: 'transparent',
	cursor: 'pointer',
	borderRadius: token('space.050'), // 4px
	width: token('space.400'), // 32px
	height: token('space.400'), // 32px
	padding: '0',
	color: wacToken('color.neutral.light800'),
	textDecoration: 'none',

	'&:hover:not(:disabled)': {
		color: wacToken('color.primary.blue'),
	},

	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},

	'&:disabled': {
		cursor: 'not-allowed',
		color: wacToken('color.neutral.light400'),
	},
});

const ellipsisStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: token('space.500'), // 40px cell
	height: token('space.400'), // 32px
	color: wacToken('color.primary.black'),
	opacity: ELLIPSIS_OPACITY,
	userSelect: 'none',
});

// Stands in for pages the `xs` rule hides when no regular ellipsis sits beside them.
const mobileEllipsisStyles = css({
	display: 'none',
	[media.below.xs]: {
		display: 'flex',
	},
});

const ELLIPSIS = '\u2026';

type PageRangeItem = {
	type: 'page' | 'ellipsis' | 'mobile-ellipsis';
	page?: number;
	index?: number;
	key: string;
	/** True for a page the `xs` rule hides: not first, last or current. */
	hiddenOnMobile?: boolean;
};

/**
 * Indices the control always shows: the first page, the last page, and a window of
 * three around the current page (clamped to the ends). Everything between two shown
 * indices collapses to an ellipsis when it hides two or more pages. A single hidden
 * page renders as itself, because an ellipsis that stands for one number is noise.
 */
const getAlwaysVisibleIndices = (totalPages: number, current: number) => {
	const lastIndex = totalPages - 1;
	const visible = new Set<number>([0, lastIndex]);

	let windowStart: number;
	if (current <= 2) {
		windowStart = 1;
	} else if (current >= lastIndex - 2) {
		windowStart = lastIndex - 3;
	} else {
		windowStart = current - 1;
	}

	for (let i = windowStart; i < windowStart + 3; i++) {
		if (i >= 0 && i <= lastIndex) {
			visible.add(i);
		}
	}

	return visible;
};

const markMobileHiddenPages = (
	items: PageRangeItem[],
	current: number,
	lastIndex: number,
): PageRangeItem[] => {
	const marked = items.map((item) =>
		item.type === 'page' && item.index !== 0 && item.index !== lastIndex && item.index !== current
			? { ...item, hiddenOnMobile: true }
			: item,
	);

	// Insert a mobile-only ellipsis after each hidden run that has no real ellipsis
	// on either side, so the hidden pages still read as a gap on small screens.
	// A run beside a real ellipsis merges into that gap. A lone hidden page with no
	// ellipsis beside it stays visible, because an ellipsis never stands for one page.
	const result: PageRangeItem[] = [];
	let runStart = -1;
	marked.forEach((item, position) => {
		const previous = result[result.length - 1];
		const isHidden = item.type === 'page' && item.hiddenOnMobile === true;
		if (isHidden && runStart === -1) {
			runStart = position;
		}
		if (!isHidden && runStart !== -1) {
			const before = marked[runStart - 1];
			const hasNeighbourEllipsis = before?.type === 'ellipsis' || item.type === 'ellipsis';
			const runLength = position - runStart;
			if (!hasNeighbourEllipsis && runLength === 1 && previous !== undefined) {
				result[result.length - 1] = { ...previous, hiddenOnMobile: undefined };
			} else if (!hasNeighbourEllipsis && previous !== undefined) {
				result.push({
					type: 'mobile-ellipsis',
					key: `mobile-ellipsis-${runStart}`,
				});
			}
			runStart = -1;
		}
		result.push(item);
	});

	return result;
};

const createPageRange = (pages: number[], selectedIndex: number): PageRangeItem[] => {
	const totalPages = pages.length;
	if (totalPages === 0) {
		return [];
	}
	const lastIndex = totalPages - 1;
	const current = Math.min(Math.max(selectedIndex, 0), lastIndex);
	const alwaysVisible = getAlwaysVisibleIndices(totalPages, current);

	const result: PageRangeItem[] = [];
	let hiddenRun: number[] = [];

	const flushHiddenRun = () => {
		if (hiddenRun.length >= 2) {
			result.push({
				type: 'ellipsis',
				key: `ellipsis-${hiddenRun[0]}`,
			});
		} else {
			hiddenRun.forEach((index) => {
				result.push({
					type: 'page',
					page: pages[index],
					index,
					key: `page-${index}`,
				});
			});
		}
		hiddenRun = [];
	};

	for (let index = 0; index <= lastIndex; index++) {
		if (alwaysVisible.has(index)) {
			flushHiddenRun();
			result.push({
				type: 'page',
				page: pages[index],
				index,
				key: `page-${index}`,
			});
		} else {
			hiddenRun.push(index);
		}
	}
	flushHiddenRun();

	return markMobileHiddenPages(result, current, lastIndex);
};

export const Pagination: FC<PaginationProps> = ({
	pages = [],
	selectedIndex = 0,
	label,
	pageLabel,
	previousLabel,
	nextLabel,
	onChange,
}) => {
	const { formatMessage } = useIntl();

	// Use provided props or fall back to localized defaults
	const ariaLabels = {
		navigation:
			label ||
			formatMessage({
				id: 'wac.design.pagination.navigation.label',
				defaultMessage: 'Pagination navigation',
			}),
		page:
			pageLabel ||
			formatMessage({
				id: 'wac.design.pagination.page.label',
				defaultMessage: 'Page',
			}),
		previous:
			previousLabel ||
			formatMessage({
				id: 'wac.design.pagination.previous.label',
				defaultMessage: 'Previous page',
			}),
		next:
			nextLabel ||
			formatMessage({
				id: 'wac.design.pagination.next.label',
				defaultMessage: 'Next page',
			}),
	};

	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (pages.length === 0) {
		return null;
	}

	const fireAnalytics = (label: string) => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'pagination',
			source: 'pagination',
			attributes: { label },
		}).fire();
	};

	const handlePageClick = (event: SyntheticEvent, pageIndex: number) => {
		if (pageIndex === selectedIndex) {
			return;
		}
		const page = pages[pageIndex];
		if (page !== undefined) {
			fireAnalytics(`page ${page}`);
			onChange?.(event, page);
		}
	};

	const handlePreviousClick = (event: SyntheticEvent) => {
		if (selectedIndex === 0) {
			return;
		}
		const page = pages[selectedIndex - 1];
		if (page !== undefined) {
			fireAnalytics('previous');
			onChange?.(event, page);
		}
	};

	const handleNextClick = (event: SyntheticEvent) => {
		if (selectedIndex === pages.length - 1) {
			return;
		}
		const page = pages[selectedIndex + 1];
		if (page !== undefined) {
			fireAnalytics('next');
			onChange?.(event, page);
		}
	};

	const pageRange = createPageRange(pages, selectedIndex);
	const isPreviousDisabled = selectedIndex === 0;
	const isNextDisabled = selectedIndex === pages.length - 1;

	return (
		<nav aria-label={ariaLabels.navigation} css={baseStyles}>
			<div css={innerContainerStyles}>
				{/* Previous Button */}
				<button
					type="button"
					css={navigatorStyles}
					onClick={handlePreviousClick}
					disabled={isPreviousDisabled}
					aria-label={ariaLabels.previous}
				>
					<ChevronLeftIcon label="" />
				</button>

				{/* Page Numbers Container */}
				<div css={pagesContainerStyles}>
					{pageRange.map((item) => {
						if (item.type === 'ellipsis') {
							return (
								<span
									key={item.key}
									css={[ellipsisStyles, numberTextStyles]}
									aria-hidden="true"
									data-testid="pagination-ellipsis"
								>
									{ELLIPSIS}
								</span>
							);
						}

						if (item.type === 'mobile-ellipsis') {
							return (
								<span
									key={item.key}
									css={[ellipsisStyles, numberTextStyles, mobileEllipsisStyles]}
									aria-hidden="true"
									data-testid="pagination-mobile-ellipsis"
								>
									{ELLIPSIS}
								</span>
							);
						}

						const isSelected = item.index === selectedIndex;
						const pageNumber = item.page;
						const pageIndex = item.index;

						if (pageNumber === undefined || pageIndex === undefined) {
							return null;
						}

						return (
							<button
								key={item.key}
								type="button"
								css={[
									pageButtonStyles,
									numberTextStyles,
									item.hiddenOnMobile ? mobileHiddenStyles : undefined,
								]}
								onClick={(event) => handlePageClick(event, pageIndex)}
								data-testid="pagination-page"
								data-mobile-hidden={item.hiddenOnMobile ? 'true' : undefined}
								aria-current={isSelected ? 'page' : undefined}
								aria-label={`${ariaLabels.page} ${pageNumber}${isSelected ? ', current page' : ''}`}
							>
								{pageNumber}
							</button>
						);
					})}
				</div>

				{/* Next Button */}
				<button
					type="button"
					css={navigatorStyles}
					onClick={handleNextClick}
					disabled={isNextDisabled}
					aria-label={ariaLabels.next}
				>
					<ChevronRightIcon label="" />
				</button>
			</div>
		</nav>
	);
};
